import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  effect,
  EventEmitter,
  input,
  Input,
  InputSignal,
  OnChanges,
  Output,
  QueryList,
  signal,
  SimpleChange,
  SimpleChanges,
  Type,
  ViewChildren,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  PcBinaryDecisionComponent,
  PcCheckboxComponent,
  PcDnpeLookupComponent,
  PcFileUploadComponent,
  PcFormFieldComponent,
  PcRadioComponent,
  PcSelectSubSelectComponent,
  PcTermsAndConditionsComponent
} from '@k12/forms';
import type { PcReviewComponent } from '../forms/pc-review/pc-review.component';
import { ComponentType, ProgramReview, PromptComponent, SubSelectRemoveButton } from '@k12/models';


@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  selector: 'lib-prompt-renderer',
  styleUrl: './prompt-renderer.component.scss',
  templateUrl: './prompt-renderer.component.html'
})
export class PromptRendererComponent implements AfterViewInit, OnChanges {
  @ViewChildren('inputContainer', { read: ViewContainerRef }) inputContainer!: QueryList<ViewContainerRef>;

  @Input({ required: true }) promptComponentInput!: PromptComponent;
  @Input() formGroup!: FormGroup;
  @Input() allowPointerEvents!: boolean;
  @Input() isAdmin: boolean = false;
  @Input() isCreate: boolean = false;
  @Input() isDrawerOpen: boolean = false;
  @Input() isIneligible: boolean = false;
  public isInputNavigationInvalid = input<boolean[]>([]);
  @Input() editFormFieldInput!: boolean;
  @Input() enableAddTextInputButton!: boolean;
  @Input() enrollmentBuilderView!: boolean;
  /**
   * The enrollment session a file_upload component uploads against. Empty in the admin builder,
   * where components are previewed rather than answered, which is what keeps that surface from
   * attempting an upload.
   */
  @Input() sessionId: string = '';
  @Input() removeButtonIndex = signal<SubSelectRemoveButton>({} as SubSelectRemoveButton);
  public reviewData: InputSignal<ProgramReview> = input(<ProgramReview>{});
  @Input() showHeaderAndInstructions: boolean = true;
  @Input() set showEditSelect(value: boolean | undefined) {
    this.isEditSelect.set(value);
  }

  @Output() cbFormFieldEmitter = new EventEmitter();
  @Output() addNewFormFieldEmitter = new EventEmitter();
  @Output() addRowDataEmitter = new EventEmitter();
  @Output() agreementSelectEmitter = new EventEmitter();
  @Output() buttonSelectEmitter = new EventEmitter();
  @Output() checkboxSelectEmitter = new EventEmitter();
  @Output() decisionEmitter = new EventEmitter();
  @Output() deleteSelectEmitter = new EventEmitter();
  @Output() dnpeLookupEmitter = new EventEmitter();
  @Output() editSectionEmitter = new EventEmitter();
  @Output() editSelectEmitter = new EventEmitter();
  @Output() openDrawerEmitter = new EventEmitter();
  @Output() removeFormFieldEmitter = new EventEmitter();
  @Output() removeRowDataEmitter = new EventEmitter();
  @Output() resetFormFieldValuesEmitter = new EventEmitter();
  @Output() selectSubSelectCheckboxEmitter = new EventEmitter();
  @Output() fileUploadValuesEmitter = new EventEmitter();

  public ComponentType: typeof ComponentType = ComponentType;
  public isEditSelect: WritableSignal<boolean | undefined> = signal(undefined);

  private childComponent:
    | ComponentRef<
        | PcBinaryDecisionComponent
        | PcCheckboxComponent
        | PcDnpeLookupComponent
        | PcFileUploadComponent
        | PcFormFieldComponent
        | PcRadioComponent
        | PcReviewComponent
        | PcSelectSubSelectComponent
        | PcTermsAndConditionsComponent
      >
    | undefined;

  // The review component is authored with no inputs of its own, so it is created with an empty list.
  // It reads the answers to display from the reviewData signal, which is set by the parent of this
  // renderer. That signal is read once at creation, so if it is empty then the child will render
  // blank with a console warning. The effect in the constructor re-reads it on every write, so
  // any answers that arrive after creation will reach the child.
  private readonly loadPcReviewComponent = () =>
    import('../forms/pc-review/pc-review.component').then((m) => m.PcReviewComponent);

  constructor() {
    // reviewData is a signal input, so it never appears in SimpleChanges and ngOnChanges cannot
    // forward it. setReviewComponent reads it once at creation, which means a payload that lands
    // after the child is mounted would never reach it. setInput is a no-op for an unchanged value,
    // so re-running this on every reviewData write is safe.
    effect(() => {
      const reviewData = this.reviewData();
      if (this.promptComponentInput?.componentType === ComponentType.Review) {
        this.childComponent?.setInput('reviewData', reviewData);
      }
    });
  }

  public ngAfterViewInit() {
    this.generatePromptInput(true);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes['promptComponentInput'] && !changes['promptComponentInput'].firstChange &&
      changes['promptComponentInput'].currentValue !== changes['promptComponentInput'].previousValue
    ) {
      this.refreshChildComponent(changes['promptComponentInput']);
    }

    if (
      changes['showEditSelect'] &&
      changes['showEditSelect'].currentValue !== changes['showEditSelect'].previousValue &&
      this.promptComponentInput.componentType === ComponentType.SelectSubSelect
    ) {
      this.childComponent?.setInput('showEditButton', this.isEditSelect());
    }

    // The session id resolves after the first prompt renders, so the file upload child is
    // created before it exists. Without this it would keep the empty initial value and have
    // nothing to upload against.
    if (
      changes['sessionId'] &&
      changes['sessionId'].currentValue !== changes['sessionId'].previousValue &&
      this.promptComponentInput.componentType === ComponentType.FileUpload
    ) {
      this.childComponent?.setInput('sessionId', this.sessionId);
    }

    if (
      (changes['enableAddTextInputButton'] &&
        changes['enableAddTextInputButton'].currentValue !== changes['enableAddTextInputButton'].previousValue &&
        this.promptComponentInput.componentType === ComponentType.SingleFormField) ||
      this.promptComponentInput.componentType === ComponentType.HorizontalFormField ||
      this.promptComponentInput.componentType === ComponentType.VerticalFormField ||
      this.promptComponentInput.componentType === ComponentType.CheckboxFormField ||
      this.promptComponentInput.componentType === ComponentType.RadioFormField
    ) {
      this.childComponent?.setInput('enableAddTextInputButton', this.enableAddTextInputButton);
    }

    if (
      (changes['editFormFieldInput'] &&
        changes['editFormFieldInput'].currentValue !== changes['editFormFieldInput'].previousValue &&
        this.promptComponentInput.componentType === ComponentType.SingleFormField) ||
      this.promptComponentInput.componentType === ComponentType.HorizontalFormField ||
      this.promptComponentInput.componentType === ComponentType.VerticalFormField ||
      this.promptComponentInput.componentType === ComponentType.CheckboxFormField ||
      this.promptComponentInput.componentType === ComponentType.RadioFormField
    ) {
      this.childComponent?.setInput('editFormFieldInput', this.enableAddTextInputButton);
    }
  }

  // Recreate the child when the row is in create mode, when no child exists yet (e.g.
  // ngAfterViewInit ran before inputs were ready), or when the component type changed for this
  // reused renderer row (query-param-only navigation reuses the same instance). A stale-type or
  // missing child would otherwise render blank with no console error.
  private refreshChildComponent(change: SimpleChange): void {
    const prev = change.previousValue as PromptComponent | undefined;
    const curr = change.currentValue as PromptComponent | undefined;
    const mustRecreate = this.isCreate || !this.childComponent || prev?.componentType !== curr?.componentType;
    if (mustRecreate) {
      this.resetChildContainers();
      this.childComponent = undefined;
      this.generatePromptInput(true);
    } else {
      this.generatePromptInput(false);
    }
  }

  public resetChildContainers(): void {
    this.inputContainer.forEach((container) => {
      container.clear();
    });
  }

  private generatePromptInput(createComponent: boolean) {
    this.inputContainer.forEach((promptInput: ViewContainerRef) => {
      const componentType = this.promptComponentInput.componentType;

      // Review renders the answers handed to it through reviewData, so it is authored with no inputs
      // of its own -- both the seeded programs and the admin builder create it with an empty list.
      // Gating it on that list is what left the review step blank with only a console warning.
      if (componentType !== ComponentType.Review && !this.promptComponentInput.inputs) {
        console.warn(
          'PromptRendererComponent: component has no inputs, nothing rendered',
          componentType,
          this.promptComponentInput?.id
        );
        return;
      }

      switch (componentType) {
        case ComponentType.BinaryDecision:
        case ComponentType.YesNoDecision:
          this.setBinaryDecisionComponent(promptInput, createComponent);
          break;
        case ComponentType.FileUpload:
          this.setFileUploadComponent(promptInput, createComponent);
          break;
        case ComponentType.SingleFormField:
        case ComponentType.HorizontalFormField:
        case ComponentType.VerticalFormField:
          this.setFormFieldComponent(promptInput, createComponent);
          break;
        case ComponentType.SelectSubSelect:
          this.setSelectSubSelectComponent(promptInput, createComponent);
          break;
        case ComponentType.TermsAndConditions:
          this.setTermsAndConditionsComponent(promptInput, createComponent);
          break;
        case ComponentType.Review:
          void this.setReviewComponent(promptInput, createComponent);
          break;
        case ComponentType.CheckboxFormField:
          this.setCheckboxLikeComponent(promptInput, createComponent, PcCheckboxComponent);
          break;
        case ComponentType.RadioFormField:
          this.setCheckboxLikeComponent(promptInput, createComponent, PcRadioComponent);
          break;
        case ComponentType.DnpeLookup:
          this.setDnpeLookupComponent(promptInput, createComponent);
          break;
        default:
          console.error('Mismatched input type');
          break;
      }
    });
  }

  private setBinaryDecisionComponent(promptInput: ViewContainerRef, createComponent: boolean): void {
    if (createComponent || !this.childComponent) {
      this.childComponent = promptInput.createComponent(PcBinaryDecisionComponent);
    }
    if (this.childComponent) {
      this.childComponent.setInput('configJson', this.promptComponentInput);
      this.childComponent.setInput('allowPointerEvents', this.allowPointerEvents);
      this.childComponent.setInput('formGroup', this.formGroup);
      (this.childComponent.instance as PcBinaryDecisionComponent).decisionEvent.subscribe((event: unknown) => {
        this.decisionEmitter.emit(event);
      });
    }
  }

  private setFileUploadComponent(promptInput: ViewContainerRef, createComponent: boolean): void {
    if (createComponent || !this.childComponent) {
      this.childComponent = promptInput.createComponent(PcFileUploadComponent);
      const fileUpload = this.childComponent.instance as PcFileUploadComponent;
      fileUpload.fileUploadValuesEmitter.subscribe((event: unknown) => {
        this.fileUploadValuesEmitter.emit(event);
      });
    }
    if (this.childComponent) {
      this.childComponent.setInput('configJson', this.promptComponentInput);
      this.childComponent.setInput('allowPointerEvents', this.allowPointerEvents);
      this.childComponent.setInput('enrollmentBuilderView', this.enrollmentBuilderView);
      this.childComponent.setInput('formGroup', this.formGroup);
      this.childComponent.setInput('sessionId', this.sessionId);
    }
  }

  private setFormFieldComponent(promptInput: ViewContainerRef, createComponent: boolean): void {
    if (createComponent || !this.childComponent) {
      this.childComponent = promptInput.createComponent(PcFormFieldComponent);
      const formField = this.childComponent.instance as PcFormFieldComponent;
      formField.openDrawer.subscribe((event: unknown) => {
        this.openDrawerEmitter.emit(event);
      });
      formField.resetFormFieldValues.subscribe((event: unknown) => {
        this.resetFormFieldValuesEmitter.emit(event);
      });
      formField.addNewFormField.subscribe((event: unknown) => {
        this.addNewFormFieldEmitter.emit(event);
      });
      formField.removeFormField.subscribe((event: unknown) => {
        this.removeFormFieldEmitter.emit(event);
      });
    }
    if (this.childComponent) {
      this.childComponent.setInput('configJson', this.promptComponentInput);
      this.childComponent.setInput('enrollmentBuilderView', this.enrollmentBuilderView);
      this.childComponent.setInput('formGroup', this.formGroup);
    }
  }

  private setTermsAndConditionsComponent(promptInput: ViewContainerRef, createComponent: boolean): void {
    if (createComponent || !this.childComponent) {
      this.childComponent = promptInput.createComponent(PcTermsAndConditionsComponent);
    }
    if (this.childComponent) {
      this.childComponent.setInput('configJson', this.promptComponentInput);
      this.childComponent.setInput('allowPointerEvents', this.allowPointerEvents);
      this.childComponent.setInput('enrollmentBuilderView', this.enrollmentBuilderView);
      this.childComponent.setInput('formGroup', this.formGroup);
      (this.childComponent.instance as PcTermsAndConditionsComponent).checkboxSelect.subscribe((event: unknown) => {
        this.checkboxSelectEmitter.emit(event);
      });
    }
  }

  private async setReviewComponent(promptInput: ViewContainerRef, createComponent: boolean): Promise<void> {
    const PcReviewComponent = await this.loadPcReviewComponent();

    if (createComponent || !this.childComponent) {
      this.childComponent = promptInput.createComponent(PcReviewComponent);
    }
    if (this.childComponent) {
      this.childComponent.setInput('enrollmentBuilderView', this.enrollmentBuilderView ?? false);
      this.childComponent.setInput('reviewData', this.reviewData());
      (this.childComponent.instance as PcReviewComponent).editSectionOutput.subscribe((event: unknown) => {
        this.editSectionEmitter.emit(event);
      });
    }
  }

  private setSelectSubSelectComponent(promptInput: ViewContainerRef, createComponent: boolean): void {
    if (createComponent) {
      this.childComponent = promptInput.createComponent(PcSelectSubSelectComponent);
      const selectSubSelect = this.childComponent.instance as PcSelectSubSelectComponent;
      selectSubSelect.editButtonSelect.subscribe((event: unknown) => {
        this.editSelectEmitter.emit(event);
      });
      selectSubSelect.buttonSelect.subscribe((event: unknown) => {
        this.buttonSelectEmitter.emit(event);
      });
      selectSubSelect.checkboxSelect.subscribe((event: unknown) => {
        this.selectSubSelectCheckboxEmitter.emit(event);
      });
      selectSubSelect.deleteButtonSelect.subscribe((event: unknown) => {
        this.deleteSelectEmitter.emit(event);
      });
    }
    if (this.childComponent) {
      this.childComponent.setInput('configJson', this.promptComponentInput);
      this.childComponent.setInput('allowPointerEvents', this.allowPointerEvents);
      this.childComponent.setInput('showEditButton', this.isEditSelect());
      this.childComponent.setInput('inputNavigationInvalid', this.isInputNavigationInvalid());
    }
  }

  private setDnpeLookupComponent(promptInput: ViewContainerRef, createComponent: boolean): void {
    if (createComponent || !this.childComponent) {
      this.childComponent = promptInput.createComponent(PcDnpeLookupComponent);
    }
    if (this.childComponent) {
      this.childComponent.setInput('configJson', this.promptComponentInput);
      this.childComponent.setInput('enrollmentBuilderView', this.enrollmentBuilderView);
      this.childComponent.setInput('formGroup', this.formGroup);
      (this.childComponent.instance as PcDnpeLookupComponent).dnpeLookup.subscribe((event: unknown) => {
        this.dnpeLookupEmitter.emit(event);
      });
    }
  }

  // CheckboxFormField and RadioFormField share identical wiring -- both expose the same generic
  // cbConfig/checkboxSelect/openDrawer outputs and inputs -- so this consolidates their switch
  // cases into a single call, differing only in which component class gets instantiated.
  private setCheckboxLikeComponent(
    promptInput: ViewContainerRef,
    createComponent: boolean,
    componentType: Type<PcCheckboxComponent | PcRadioComponent>
  ): void {
    if (createComponent || !this.childComponent) {
      this.childComponent = promptInput.createComponent(componentType);
      const checkboxLike = this.childComponent.instance as PcCheckboxComponent | PcRadioComponent;
      checkboxLike.cbConfig.subscribe((event: unknown) => {
        this.cbFormFieldEmitter.emit(event);
      });
      checkboxLike.checkboxSelect.subscribe((event: unknown) => {
        this.checkboxSelectEmitter.emit(event);
      });
      checkboxLike.openDrawer.subscribe((event: unknown) => {
        this.openDrawerEmitter.emit(event);
      });
    }
    if (this.childComponent) {
      this.childComponent.setInput('configJson', this.promptComponentInput);
      this.childComponent.setInput('enrollmentBuilderView', this.enrollmentBuilderView);
      this.childComponent.setInput('allowPointerEvents', this.allowPointerEvents);
      this.childComponent.setInput('formGroup', this.formGroup);
    }
  }
}
